<template>
  <div id="app">
    <!-- fixed element -->
    <logo
      v-if="!loaderIsOpen"
      class="is-fixed--top-left"/>
    <hamburger
      v-if="!loaderIsOpen"
      @click.native="menuToggle"
      class="is-fixed--top-right"/>
    <sound-button
      v-if="!loaderIsOpen"
      class="is-fixed--bot-right"/>
    <copyright
      v-if="!loaderIsOpen"
      class="is-fixed--bot-left"/>

    <!-- arrow -->
    <arrow
      v-if="topArrowVisibility"
      :icon="!isInHome"
      :text="topArrowText"
      class="is-fixed--top-center"
      @click.native="topArrowClick"/>

    <arrow
      inverted
      v-if="bottomArrowVisibility"
      :text="bottomArrowText"
      class="is-fixed--bot-center"
      @click.native="bottomArrowClick"/>

    <!-- webgl -->
    <webgl/>

    <!--main content-->
    <div class="view-container">
      <transition @leave="heroLeave" mode="out-in">
        <loader v-if="loaderIsOpen && fontLoaded"/>
        <hero v-else-if="!loaderIsOpen && !menuIsOpen && !isInContact"/>
        <Contact v-else-if="!loaderIsOpen && !menuIsOpen && isInContact"/>
        <AppMenu v-else-if="!loaderIsOpen && menuIsOpen"/>
      </transition>

      <transition
        mode="out-in"
        @before-leave="pageBeforeLeave"
        @leave="pageLeave">
        <router-view
          v-if="!isInHome && !loaderIsOpen && !menuIsOpen"
          :key="$store.getters.getCurrentPage.path"/>
      </transition>
    </div>

    <!-- cursor -->
    <app-cursor v-if="!is_touch_device && !isEdge"/>

    <!-- cookie -->
    <cookie-bar
      class="app__cookie">
      <cookie-content
        slot="content"
        text=""
        url="https://www.iubenda.com/privacy-policy/18990984/cookie-policy"
        label="This website employs <strong class='cookie__label'>cookies</strong> to improve the browsing experience."
      />
      <cookie-button
        slot="button"
        label="Accept"
      />
    </cookie-bar>

    <!-- footer -->
    <app-footer/>
  </div>
</template>

<script>
// utils
import { mapGetters, mapMutations, mapState } from 'vuex';
import FontFaceObserver from 'fontfaceobserver';
import anime from 'animejs';
import hades from '@/scripts/hades';
import engine from '@/scripts/engine';
import medusa from '@/scripts/medusa';

import { isEdge } from '@/scripts/utils';

// components
import Hero from '@/components/Hero.vue';
import Arrow from '@/components/AppArrow.vue';
import Loader from '@/components/AppLoader.vue';
import Hamburger from '@/components/AppHamburger.vue';
import AppMenu from '@/components/AppMenu.vue';
import AppFooter from '@/components/AppFooter.vue';
import Logo from '@/components/AppLogo.vue';
import SoundButton from '@/components/AppSoundButton.vue';
import Webgl from '@/components/WebGL.vue';
import store from '@/store';
import Copyright from '@/components/AppCopyright.vue';
import CookieBar from '@/components/Cookie/Cookie.vue';
import CookieContent from '@/components/Cookie/CookieContent/index.vue';
import CookieButton from '@/components/Cookie/CookieButton/index.vue';
import Contact from '@/components/Contact.vue';
import AppCursor from '@/components/AppCursor.vue';

export default {
  name: 'App',
  components: {
    AppCursor,
    Contact,
    AppFooter,
    Copyright,
    AppMenu,
    Webgl,
    SoundButton,
    Hamburger,
    Loader,
    Arrow,
    Hero,
    Logo,
    CookieBar,
    CookieContent,
    CookieButton,
  },
  data() {
    return {
      nearTop: true,
      beforeHalfWindow: false,
      wheelUp: false,
    };
  },

  //  COMPUTED
  computed: {
    ...mapState([
      'fontLoaded',
      'resourcesLoaded',
      'soundLoaded',
      'loaderIsOpen',
      'slideInTransition',
      'blobInTransition',
      'menuIsOpen',
      'sound',
      'footerIsOpen',
    ]),
    ...mapGetters([
      'getPrevPage',
      'getNextPage',
    ]),
    isInHome() {
      return this.$route.name === 'Home';
    },
    isInContact() {
      return this.$route.name === 'Contact';
    },
    topArrowText() {
      return `chapter ${this.getPrevPage.chapter}`;
    },
    topArrowVisibility() {
      if (this.loaderIsOpen || this.menuIsOpen) return false;
      if (this.wheelUp) return true;
      return this.nearTop;
    },
    bottomArrowVisibility() {
      return this.resourcesLoaded && this.soundLoaded && this.nearTop && !this.menuIsOpen && !this.isInContact;
    },
    bottomArrowText() {
      if (this.loaderIsOpen) return 'click to start';
      if (!this.isInHome) return 'scroll to discover';
      return `chapter ${this.getNextPage.chapter}`;
    },
    isEdge() {
      return isEdge();
    },
    is_touch_device() {
      const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');
      const mq = query => window.matchMedia(query).matches;

      // eslint-disable-next-line no-mixed-operators,no-undef
      if (('ontouchstart' in window) || window.DocumentTouch && document instanceof DocumentTouch) {
        return true;
      }
      const query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('');
      return mq(query);
    },
  },

  /* METHODS */
  methods: {
    ...mapMutations([
      'stopLoadingFont',
      'stopLoadingSound',
      'closeLoader',
      'goNextPage',
      'goPrevPage',
      'toggleMenu',
      'toggleFooter',
    ]),
    menuToggle() {
      this.toggleMenu();
      if (this.footerIsOpen) this.toggleFooter();
    },

    resize() {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    },
    handleVisibilityChange() {
      if (document.hidden) {
        this.sound.stop();
      } else {
        this.sound.play();
      }
    },

    initUtils() {
      // targets-medusa
      medusa.init();

      // hades
      const DOMElement = document.querySelector('#app');
      const DOMContainer = document.querySelector('.view-container');

      hades.init(DOMElement, DOMContainer);
      hades.ref.scrollTo({ y: 0 }, 0);
      hades.ref.play();

      // engine
      // check if the scroll offset is more than 20px
      engine.add(() => {
        this.wheelUp = hades.ref.direction.y > 0;
        this.nearTop = hades.ref.amount.y < 20;
        this.beforeHalfWindow = hades.ref.amount.y < window.innerHeight / 2;
      }, 'near-top');

      // sound
      this.sound.play();
      this.sound.fade(0, 0.3, 500);
      document.addEventListener('visibilitychange', this.handleVisibilityChange, false);
    },
    startNavigation() {
      this.$eventHub.$off('startNavigation', this.startNavigation);
      this.$eventHub.$emit('morph', this.pageIndex);
      this.initUtils();
      this.closeLoader();
    },
    topArrowClick() {
      if (this.isInHome && !this.slideInTransition) this.goPrevPage();
      else this.$router.push('/');
    },
    bottomArrowClick() {
      if (this.loaderIsOpen) {
        this.startNavigation();
      } else if (!this.isInHome) {
        hades.ref.scrollTo({ y: window.innerHeight }, 1500);
      } else if (!this.slideInTransition) {
        this.goNextPage();
      }
    },

    heroLeave(el, done) {
      hades.ref.pause();

      anime({
        targets: el,
        opacity: {
          value: 0,
          easing: 'cubicBezier(0.165, 0.84, 0.44, 1)',
        },
        translateY: {
          value: '-20vh',
          easing: 'cubicBezier(0.895, 0.03, 0.685, 0.22)',
        },
        duration: 2000,
        complete() {
          hades.ref.scrollTo({ y: 0 }, 0);
          hades.ref.play();
          done();
        },
      });
    },

    pageLeave(el, done) {
      hades.ref.pause();

      anime({
        targets: el,
        translateY: {
          value: -400,
          delay: 300,
        },
        opacity: 0,
        duration: 1500,
        delay: 100,
        easing: 'easeInOutSine',
        complete: () => {
          hades.ref.scrollTo({ y: 0 }, 0);
          this.$nextTick(() => {
            store.commit('togglePageTransition', false);
            hades.ref.play();
            setTimeout(() => done(), 200);
          });
        },
      });
    },

    pageBeforeLeave() {
      store.commit('togglePageTransition', true);
    },
  },
  created() {
    this.$eventHub.$on('startNavigation', this.startNavigation);
  },
  mounted() {
    this.resize();
    window.addEventListener('resize', () => this.resize());

    // font observer
    const PlayfairNormal = new FontFaceObserver('Playfair Display', {
      weight: 'normal',
    });
    const KarlaNormal = new FontFaceObserver('Karla', {
      weight: 'normal',
    });

    Promise.all([PlayfairNormal.load(), KarlaNormal.load()])
      .then(() => {
        this.$nextTick(() => {
          this.stopLoadingFont();
        });
      });

    // sound observer
    this.sound.once('load', () => {
      this.stopLoadingSound();
    });
  },
};
</script>

<style lang="scss">
@import "./styles/base.scss";
@import "./styles/static.scss";

$vertical-padding: 5.2vw;
$horizontal-padding: 4.4vw;

$vertical-padding-tablet: 9.2vw;
$horizontal-padding-tablet: 5.4vw;

$vertical-padding-mobile: 10.2vw;
$horizontal-padding-mobile: 6.7vw;

@mixin flex-position($vertical, $horizontal) {
  @extend .is-fixed;

  @if ($horizontal == center) {
    #{$vertical}: 0;
  } @else {
    #{$vertical}: $vertical-padding-mobile;
    @include mq(mobileL) {
      #{$vertical}: $vertical-padding-tablet;
    }
    @include mq(ds) {
      #{$vertical}: $vertical-padding;
    }
  }

  @if ($horizontal == center) {
    z-index: 5;
    transform: translateX(-50%);
  } @else {
    #{$horizontal}: $horizontal-padding-mobile;
    @include mq(mobileL) {
      #{$horizontal}: $horizontal-padding-tablet;
    }
    @include mq(ds) {
      #{$horizontal}: $horizontal-padding;
    }
  }
}

html,
body,
#app {
  position: fixed;
  width: 100vw;
  overflow: hidden;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
}

@supports (-ms-ime-align:auto) {
  #app {
    overflow-y: scroll;
  }
}

#app {
  top: 0;
  left: 0;
  text-align: center;
}

.view-container {
  position: relative;
  min-height: 100%;
}

.is-fixed {
  position: fixed;
  z-index: 10;

  &--top-right {
    @include flex-position(top, right);
  }

  &--top-left {
    @include flex-position(top, left);
  }

  &--bot-right {
    @include flex-position(bottom, right);
  }

  &--bot-left {
    @include flex-position(bottom, left);
  }

  &--top-center {
    @include flex-position(top, center);
  }

  &--bot-center {
    @include flex-position(bottom, center);
    @include mq($until: ipadP) {
      width: 100%
    }
  }
}
</style>
